import { Component, EventEmitter, Input, Output, OnInit, forwardRef } from '@angular/core';
import { ThemeService } from '../services/theme.service';
import { Attachment } from '@shared/entities/attachment';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-file-input',
  templateUrl: './file-input.component.html',
  styleUrls: ['./file-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileInputComponent),
      multi: true
    }
  ]
})
export class FileInputComponent implements OnInit, ControlValueAccessor {
  @Input() accept = '.pdf,.doc,.docx';
  @Input() multiple = false;
  @Input() labelKey!: string;
  @Input() showTitle = false;
  @Input() titleKey!: string;
  @Input() infoPopover?: string;
  @Input() hideLabel = false;

  @Input() typesPopoverLabel?: string;
  @Input() existingFile?: Attachment | { fileName?: string } | null;

  @Output() fileSelected = new EventEmitter<File | File[] | null>();
  @Output() fileRemoved = new EventEmitter<void>();

  selectedFile?: File;
  selectedFiles?: File[];

  private onChange: (v: any) => void = () => {
    // Implementation
  };
  private onTouched: () => void = () => {
    // Implementation
  };
  disabled = false;

  readonly inputId = 'documentFile-' + Math.random().toString(36).slice(2, 9);

  constructor(private _themeService: ThemeService) {}

  ngOnInit(): void {
    const missing: string[] = [];
    if (!this.labelKey || !this.labelKey.toString().trim()) missing.push('labelKey');
    if (missing.length) {
      throw new Error(`FileInputComponent: missing required @Input(s): ${missing.join(', ')}`);
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input || !input.files) return;
    this.existingFile = null;
    if (this.multiple) {
      this.selectedFiles = Array.from(input.files);
      this.selectedFile = undefined;
      const value = this.selectedFiles.length ? this.selectedFiles : null;
      this.fileSelected.emit(value);
      this.onChange(value);
      this.onTouched();
    } else {
      this.selectedFile = input.files[0] || undefined;
      this.selectedFiles = undefined;
      const value = this.selectedFile || null;
      this.fileSelected.emit(value);
      this.onChange(value);
      this.onTouched();
    }

    input.value = '';
  }

  writeValue(obj: any): void {
    this.selectedFile = undefined;
    this.selectedFiles = undefined;
    this.existingFile = null;

    if (!obj) return;

    if (obj instanceof File) {
      this.selectedFile = obj;
      return;
    }

    if (Array.isArray(obj) && obj.length > 0 && obj[0] instanceof File) {
      this.selectedFiles = obj as File[];
      return;
    }

    if (obj && (obj.fileName || obj.fileName === '')) {
      this.existingFile = obj as Attachment;
      return;
    }

    if (typeof obj === 'string') {
      this.existingFile = { fileName: obj } as any;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  removeFile() {
    this.selectedFile = undefined;
    this.selectedFiles = undefined;
    this.existingFile = null;
    this.fileRemoved.emit();
    this.onChange(null);
  }

  get displayName(): string {
    if (this.multiple) {
      const names = (this.selectedFiles || []).map((f) => f.name);
      if (names.length) return names.join(', ');
      if (this.existingFile && this.existingFile.fileName) return this.existingFile.fileName;
      return '';
    }

    if (this.selectedFile) return this.selectedFile.name;

    if (this.existingFile && this.existingFile.fileName) return this.existingFile.fileName || '';

    return '';
  }

  get isLight(): boolean {
    return this._themeService.isLight();
  }
}
